import { Theme, useTheme } from '@mui/material';
import { CartesianGrid, CartesianGridProps } from 'recharts';

import { buildGridConfig } from './_utils';
import { ChartGridProps } from './types';
import { getThemeColor } from '../../../../utils';
import { tokens } from '../../../../theme/generated/tokens';

const DEFAULT_GRID_LINE_STYLE: NonNullable<ChartGridProps['lineStyles']> = {
  stroke: tokens.theme.border.structural.neutral,
  strokeOpacity: 0.5,
  strokeWidth: '1px',
};

const buildCartesianGridProps = (
  gridProps: Omit<ChartGridProps, 'testId'>,
  theme: Theme
): CartesianGridProps => {
  const {
    hideHorizontal = false,
    hideVertical = false,
    lineStyles = DEFAULT_GRID_LINE_STYLE,
    verticalLineGenerator,
    horizontalLineGenerator,
    syncWithTicks = true,
    verticalFill,
    horizontalFill,
    ...rest
  } = gridProps;

  return {
    ...rest,
    ...lineStyles,
    stroke: lineStyles.stroke
      ? getThemeColor(theme, lineStyles.stroke)
      : undefined,
    // TODO: there is a type mismatch in recharts 3.8.1, grid asks for both
    // (string | number) and (string | number[])
    strokeDasharray: lineStyles.strokeDasharray as string,
    verticalFill: verticalFill?.map((color) => getThemeColor(theme, color)),
    horizontalFill: horizontalFill?.map((color) => getThemeColor(theme, color)),
    syncWithTicks,
    vertical: buildGridConfig(theme, hideVertical, verticalLineGenerator),
    horizontal: buildGridConfig(theme, hideHorizontal, horizontalLineGenerator),
  };
};

export const ChartGrid = ({
  testId = 'chart-grid-line',
  ...rest
}: ChartGridProps) => {
  const theme = useTheme();
  const cartesianGridProps = buildCartesianGridProps(rest, theme);

  return (
    <CartesianGrid
      {...cartesianGridProps}
      // recharts pass these directly to <line/> elements
      className="ChartGrid-line"
      data-testid={testId}
    />
  );
};
